/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { ProductListLine, ProductListsDataActions } from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions } from './utilities';

/**
 * Input class for the AddItemToProductList data action.
 */
export class AddItemToProductListInput implements IActionInput {
    public readonly productListId: string;

    public readonly productId: number;

    public readonly quantity: number;

    public readonly unitOfMeasure: string;
    public readonly isMultipleCatalogFeatureEnabledInHq?: boolean;
    public readonly catalogId?: number | undefined;

    public constructor(
        productListId: string,
        productId: number,
        quantity: number,
        unitOfMeasure: string,
        isMultipleCatalogFeatureEnabledInHq?: boolean,
        catalogId?: number | undefined
    ) {
        this.productListId = productListId;
        this.productId = productId;
        this.quantity = quantity;
        this.unitOfMeasure = unitOfMeasure || 'ea';
        this.isMultipleCatalogFeatureEnabledInHq = isMultipleCatalogFeatureEnabledInHq;
        this.catalogId = catalogId;
    }

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => 'AddItemToProductListInput';

    /**
     * Get Cachetype Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => 'AddItemToProductListInput';

    /**
     * Get Cachetype.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the AddItemToProductList method.
 * @param inputData - The input data passed to the createInput method.
 * @param productListId - The id of product list to update.
 * @param productId - The id of a product to add.
 * @param quantity - How many items of this product to add.
 * @param unitOfMeasure - Product measuring unit.
 * @returns - Input data for productlist add api.
 */
export const addItemToProductListInput = (
    inputData: ICreateActionContext,
    productListId: string,
    productId: number,
    quantity: number,
    unitOfMeasure: string,
    isMultipleCatalogFeatureEnabledInHq?: boolean,
    catalogId?: number | undefined
): AddItemToProductListInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create product list. User is not authenticated.');
    }
    return new AddItemToProductListInput(
        productListId,
        productId,
        quantity,
        unitOfMeasure,
        isMultipleCatalogFeatureEnabledInHq || false,
        catalogId
    );
};

/**
 * The action method for the AddItemToProductList data action.
 * @param input - The action input.
 * @param context - The action context.
 * @returns - ProductList.
 */

// Consider updating 'add-item-to-order-template.ts' if you update this function.
export async function addItemToProductListAction(input: AddItemToProductListInput, context: IActionContext): Promise<ProductListLine> {
    try {
        const catalogId = input.catalogId;
        const productListLineToAdd: ProductListLine = {
            ProductListId: input.productListId,
            ProductId: input.productId,
            Quantity: input.quantity,
            UnitOfMeasure: input.unitOfMeasure,
            ...(input.isMultipleCatalogFeatureEnabledInHq && { catalogId: catalogId })
        };

        const productListLine: ProductListLine[] = await ProductListsDataActions.addProductListLinesAsync(
            {
                callerContext: context
            },
            input.productListId,
            [productListLineToAdd]
        );

        if (ArrayExtensions.hasElements(productListLine) && productListLine[0].ProductListId) {
            return productListLine[0];
        }
        context.telemetry.error('Product list line was not returned when adding it to quick order bag');
        throw new Error('Product list line was not returned when adding it to quick order bag');
    } catch (error) {
        context.telemetry.error('Not able to add an item to quick order bag', error);
        throw error;
    }
}

/**
 * The AddItemToOrderTemplate Data Action.
 * Returns product list line that was added to the order template.
 */
export const addItemToProductListActionDataAction = createObservableDataAction<ProductListLine>({
    id: '@msdyn365-commerce-modules/retail-actions/add-item-to-product-list',
    action: addItemToProductListAction as IAction<ProductListLine>,
    input: addItemToProductListInput
});

export default addItemToProductListActionDataAction;
